import { ROOT_PATH, VISITS_SUB_PATH } from 'shared/consts';
import { useIsDesktopHost } from 'hooks/use-host-info.hook';
import { useNativeHistoryHeader } from 'hooks/use-native-history-header.hook';

import { Modal } from 'components/shared/modal';
import { useNavigate, useParams, useLocation, generatePath } from 'react-router-dom';
import { useFieldMetadata } from '../../store/ui-metadata/hooks';
import { lazy, Suspense, useCallback } from 'react';
import { OwnerParams } from 'shared/types';

const VisitModal = lazy(() => import('components/visit-modal/visit-modal'));

const getBackPath = (pathname: string): string | null => {
  const pathParts = pathname.split('/');
  const basePathIndex = pathParts.indexOf('visits');
  if (basePathIndex !== -1) {
    // Return path up to and including visitUiType (e.g., /visits/upcoming)
    return pathParts.slice(0, basePathIndex + 2).join('/');
  }
  return null;
};

const getNewVisitorPath = (ownerParams: Partial<OwnerParams>): string | null => {
  if (ownerParams.ownerId && ownerParams.ownerType) {
    return generatePath(`${ROOT_PATH}/${VISITS_SUB_PATH}/new/visitor`, {
      ownerId: ownerParams.ownerId,
      ownerType: ownerParams.ownerType,
    });
  }
  return null;
};

export const VisitDetailsPage = (): JSX.Element => {
  const { visitId, visitUiType } = useParams<{ visitId: string; visitUiType: string }>();
  const ownerParams = useParams<OwnerParams>();
  const navigate = useNavigate();
  const location = useLocation();
  const viewVisitsEnabled = useFieldMetadata('view_visits_enabled');

  const handleClose = useCallback(() => {
    if (viewVisitsEnabled && visitUiType) {
      const backPath = getBackPath(location.pathname);
      if (backPath) {
        navigate(backPath, { replace: true });
      }
    } else {
      const newVisitorPath = getNewVisitorPath(ownerParams);
      if (newVisitorPath) {
        navigate(newVisitorPath, { replace: true });
      }
    }
  }, [viewVisitsEnabled, visitUiType, navigate, location.pathname, ownerParams]);

  // A visit detail is a route of its own, so the native back arrow pops straight back to the list.
  useNativeHistoryHeader(!useIsDesktopHost());

  return (
    <Suspense fallback={null}>
      <Modal onClose={handleClose} variant="wide" hideMobileCloseButton={!viewVisitsEnabled}>
        <VisitModal visitId={visitId} />
      </Modal>
    </Suspense>
  );
};
